import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import FormItem from 'components/form/FormItem';
import LottiePlayer from 'components/global/LottiePlayer';
import Modal from 'components/global/Modal';
import * as S from 'components/global/styled/ConfirmationModal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { ENUMS, PRICE_CONFIGURATIONS } from 'services/consts';

import { setLoading } from 'stores/layoutSlice';

import AppLogger from 'utils/AppLogger';

const ChargeCorrectionCreditCardModal = NiceModal.create(
	({ id, orderNumber, manuallyChargedCreditCheck, manuallyChargedCredit }) => {
		const { t } = useTranslation();
		const dispatch = useDispatch();
		const modal = useModal();
		const axiosPrivate = useAxiosPrivate();
		const { showSuccess, showError } = useAlert();

		const schema = useMemo(
			() =>
				yup
					.object()
					.shape({
						id: yup.string().required(),
						descriptionType: yup.string().required()
					})
					.required(),
			[]
		);
		const fillForm = useCallback(async () => {
			const defaultValues = {
				id,
				descriptionType: null
			};
			return defaultValues;
		}, [id]);

		const formMethods = useForm({
			defaultValues: fillForm,
			resolver: yupResolver(schema)
		});

		const {
			handleSubmit,
			watch,
			setValue,
			formState: { isDirty, isSubmitting, isLoading: isFormLoading }
		} = formMethods;
		const isLoading = isFormLoading || isSubmitting;

		const handleCloseModal = () => {
			modal.remove();
		};

		const onSubmit = async (data) => {
			dispatch(setLoading(true));

			await axiosPrivate
				.post(PRICE_CONFIGURATIONS.CHARGE_CORRECTION_FROM_CREDIT_CARD, data)
				.then((response) => {
					if (response?.status === 200) {
						showSuccess();
						handleCloseModal();
					} else {
						showError();
						AppLogger.error(response);
					}
				})
				.catch((error) => {
					showError({
						message: error?.response?.data?.code || error?.response?.data?.message
					});
					AppLogger.error(error);
				})
				.finally(() => {
					dispatch(updateDynamicListsInPage());
				});

			dispatch(setLoading(false));
		};

		return (
			<>
				<Modal maxWidth="x-small" fullwidth closeButton={false} className="modal-confirmation">
					<S.Container>
						<S.IconContainer>
							<LottiePlayer
								name={manuallyChargedCreditCheck ? 'alert-warning-icon' : 'confirmation-modal'}
							/>
						</S.IconContainer>
						<S.HeadText>{t('chargeCorrectionFromCreditCard')}</S.HeadText>
						<S.BodyText>
							{manuallyChargedCreditCheck
								? t('chargeCorrectionToClientManuallyBiggerThanZero', {
										manuallyChargedCredit
									})
								: t('chargeCorrectionFromCreditCardConfirmation')}
							<S.FormItemWrapper>
								<FormItem label="otoOrderID">
									<StringInput value={orderNumber} disabled />
								</FormItem>
								<FormItem isLoading={isLoading} label="chargeCorrectionCreditCardReason" required>
									<EnumSelect
										searchable={false}
										enumName={ENUMS.INVOICE_CHECKER_DIFFERENCE_TYPES}
										setValue={(value) => {
											setValue('descriptionType', value, {
												shouldValidate: true,
												shouldDirty: true
											});
										}}
										value={watch('descriptionType')}
									/>
								</FormItem>
							</S.FormItemWrapper>
						</S.BodyText>
						<S.BlockDiv>
							<S.ButtonContainer>
								<Button
									onClick={() => {
										handleCloseModal();
									}}
									label={t('no')}
									variant="outline"
								/>
							</S.ButtonContainer>
							<S.ButtonContainer>
								<Button
									onClick={handleSubmit(onSubmit, (errors) => {
										AppLogger.log(errors);
									})}
									disabled={!isDirty}
									disabledTooltip={!isDirty && 'formInfo.noChanges'}
									variant="blue"
									label={'yes'}
								/>
							</S.ButtonContainer>
						</S.BlockDiv>
					</S.Container>
				</Modal>
			</>
		);
	}
);

export default ChargeCorrectionCreditCardModal;
